import { ALLIANCE_APPS, AllianceAppKey } from './alliance-apps';
import { CURRENT_APP } from './current-app';

/**
 * Page-surface colors shared by the System Screens and the Sign In page.
 * Per-app values come from the Claude Design SystemScreen and SignInPage
 * specs.
 */

/** Contained button colors: the same primary on Sign In and System Screens */
const APP_PRIMARY: Record<AllianceAppKey, { bg: string; fg: string }> = {
  arc: { bg: '#1976d2', fg: '#ffffff' },
  contracting: { bg: '#00C868', fg: '#03182E' },
  leads: { bg: '#0094FF', fg: '#001D22' },
  events: { bg: '#D32F2F', fg: '#ffffff' },
  university: { bg: '#CE0E2D', fg: '#ffffff' },
};

/** Link and ID color on light pages */
const APP_LINK_LIGHT: Record<AllianceAppKey, string> = {
  arc: '#1976d2',
  contracting: '#00874a',
  leads: '#0070c0',
  events: '#C62828',
  university: '#ff7085',
};

/** Title type on System Screens: size desktop / phone */
const APP_TITLE: Record<
  AllianceAppKey,
  {
    size: number;
    sizeMobile: number;
    lineHeight: number;
    letterSpacing: string;
  }
> = {
  arc: { size: 36, sizeMobile: 28, lineHeight: 1.15, letterSpacing: '-.01em' },
  contracting: {
    size: 36,
    sizeMobile: 28,
    lineHeight: 1.15,
    letterSpacing: '-.01em',
  },
  leads: {
    size: 36,
    sizeMobile: 28,
    lineHeight: 1.15,
    letterSpacing: '-.01em',
  },
  events: { size: 40, sizeMobile: 32, lineHeight: 1.1, letterSpacing: '0' },
  university: {
    size: 54,
    sizeMobile: 42,
    lineHeight: 0.98,
    letterSpacing: '.01em',
  },
};

/**
 * Colors for a full page in this app. University pages are always dark;
 * ARC is dark inside the shell (pass `inShell`).
 */
export const pageSurface = (params: { inShell?: boolean } = {}) => {
  const key = CURRENT_APP.key;
  const isUniversity = key === 'university';
  const dark = isUniversity || (params.inShell === true && key === 'arc');
  const darkLink = isUniversity ? '#ff8a9a' : '#4fc3f7';

  return {
    dark,
    bg: isUniversity
      ? '#1E2124'
      : dark
        ? '#161718'
        : key === 'events'
          ? '#fafafa'
          : '#fcfcfc',
    paper: isUniversity ? '#2a2e33' : dark ? '#212121' : '#ffffff',
    text: dark ? '#f3f3f3' : '#15181b',
    text2: dark ? '#ffffffb3' : '#00000099',
    border: dark ? '#ffffff1f' : '#0000001a',
    border2: dark ? '#ffffff4d' : '#00000038',
    hover: dark ? '#ffffff12' : '#0000000a',
    link: dark ? darkLink : APP_LINK_LIGHT[key],
    /** Logo color: the bar color on light pages, white on dark */
    logo: dark ? '#ffffff' : ALLIANCE_APPS[key].bar,
    primary: APP_PRIMARY[key],
    title: APP_TITLE[key],
  };
};
